import React, { useEffect, useState } from 'react';
import { Building2, MapPin, Phone, User as UserIcon, ArrowRight, type LucideIcon } from 'lucide-react';
import { procurementService } from '../../../../services/procurementService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import type { LegalEntity, Department } from '../../../../types/procurement';

interface OrderHeaderStepProps {
  operatorName: string;
  onProceed: (legalEntityNo: string, legalEntityName: string, department: Department) => void;
}

export const OrderHeaderStep: React.FC<OrderHeaderStepProps> = ({ operatorName, onProceed }) => {
  const { t } = useTranslation();
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedLegalEntityNo, setSelectedLegalEntityNo] = useState('');
  const [selectedDepartmentNo, setSelectedDepartmentNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    procurementService
      .getLegalEntities()
      .then(setLegalEntities)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedLegalEntityNo) {
      setDepartments([]);
      setSelectedDepartmentNo('');
      return;
    }
    setLoading(true);
    procurementService
      .getDepartments(selectedLegalEntityNo)
      .then((deps) => {
        setDepartments(deps);
        setSelectedDepartmentNo('');
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedLegalEntityNo]);

  const selectedLegalEntity = legalEntities.find(
    (e) => e.legalEntityNo === selectedLegalEntityNo
  );
  const selectedDepartment = departments.find((d) => d.departmentNo === selectedDepartmentNo);
  const canProceed = !!selectedLegalEntity && !!selectedDepartment;

  const handleProceed = () => {
    if (!selectedLegalEntity || !selectedDepartment) return;
    onProceed(
      selectedLegalEntity.legalEntityNo,
      selectedLegalEntity.legalEntityName,
      selectedDepartment
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
      <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
        <Building2 size={20} className="text-blue-600" />
        {t('warehouse.modules.bestillingssystem.header.selectCompanyDepartment', {}, 'Velg selskap og avdeling')}
      </h2>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.bestillingssystem.fields.company', {}, 'Selskap')}</label>
          <select
            value={selectedLegalEntityNo}
            onChange={(e) => setSelectedLegalEntityNo(e.target.value)}
            disabled={loading}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('warehouse.modules.bestillingssystem.fields.selectCompanyOption', {}, '-- Velg selskap --')}</option>
            {legalEntities.map((le) => (
              <option key={le.legalEntityNo} value={le.legalEntityNo}>
                {le.legalEntityNo} – {le.legalEntityName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.bestillingssystem.fields.department', {}, 'Avdeling')}</label>
          <select
            value={selectedDepartmentNo}
            onChange={(e) => setSelectedDepartmentNo(e.target.value)}
            disabled={loading || !selectedLegalEntityNo}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          >
            <option value="">{t('warehouse.modules.bestillingssystem.fields.selectDepartmentOption', {}, '-- Velg avdeling --')}</option>
            {departments.map((d) => (
              <option key={d.departmentNo} value={d.departmentNo}>
                {d.departmentNo} – {d.departmentName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Header info card - read-only */}
      {selectedDepartment && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">
            {t('warehouse.modules.bestillingssystem.deliveryInfo.title', {}, 'Leveringsinformasjon')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <InfoRow
              icon={Building2}
              label={t('warehouse.modules.bestillingssystem.fields.department', {}, 'Avdeling')}
              value={`${selectedDepartment.departmentNo} – ${selectedDepartment.departmentName}`}
            />
            <InfoRow icon={UserIcon} label={t('warehouse.modules.bestillingssystem.deliveryInfo.contactPerson', {}, 'Kontaktperson')} value={selectedDepartment.contactName} />
            <InfoRow icon={MapPin} label={t('warehouse.modules.bestillingssystem.deliveryInfo.address1', {}, 'Adresse 1')} value={selectedDepartment.delAddress1} />
            <InfoRow icon={MapPin} label={t('warehouse.modules.bestillingssystem.deliveryInfo.address2', {}, 'Adresse 2')} value={selectedDepartment.delAddress2} />
            <InfoRow icon={MapPin} label={t('warehouse.modules.bestillingssystem.deliveryInfo.address3', {}, 'Adresse 3')} value={selectedDepartment.delAddress3} />
            <InfoRow
              icon={MapPin}
              label={t('warehouse.modules.bestillingssystem.deliveryInfo.cityZip', {}, 'Poststed')}
              value={
                selectedDepartment.delZipCode || selectedDepartment.delCity
                  ? `${selectedDepartment.delZipCode ?? ''} ${selectedDepartment.delCity ?? ''}`.trim()
                  : null
              }
            />
            <InfoRow icon={Phone} label={t('warehouse.modules.bestillingssystem.fields.orderedBy', {}, 'Bestiller')} value={operatorName} />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleProceed}
          disabled={!canProceed}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium px-5 py-2.5 rounded-lg flex items-center gap-2"
        >
          {t('common.buttons.next', {}, 'Neste')}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

interface InfoRowProps {
  icon: LucideIcon;
  label: string;
  value?: string | null;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-gray-800 truncate">{value || '—'}</div>
    </div>
  </div>
);
